import {
  handleUpdateStudyDiagnosis,
  handleFetchDiagnosisCodes,
  STUDY_STATUS,
} from '@/entities/study';
import type { PopupScrollEvent } from '@/shared/ui/select.types';
import type { UpdateStudyDiagnosisMutation } from '@/shared/api';

import { useSetState, useUpdateEffect } from '@/shared/lib/customHooks';
import {
  showMessageError,
  showMessageInfo,
  showMessageSuccess,
  convertToMoment,
} from '@/shared/lib';
import _ from 'lodash';
import React, { useEffect, useRef } from 'react';
import { t } from 'ttag';
import type {
  DataProps,
  EditDiagnosisDrawerProps,
  MinimalState,
  StoreData,
} from './editDiagnosisDrawer.types';
import { initalState } from '../../../../config/editDiagnosisDrawer';
import { convertPayload } from '../../../../lib/editDiagnosisDrawer';
import { EDIT_DRAWER_KEY } from '../../../../config/editStudyDetails';

type UpdateStudyDiagnosisResult = NonNullable<UpdateStudyDiagnosisMutation['updateStudyDiagnosis']>;

/**
 * Drives the drawer that edits a study's primary and secondary diagnosis codes and patient
 * medications. Re-seeds its state from the study whenever the drawer opens or the diagnosis data
 * changes, fetches diagnosis codes when either search term changes, and wires field, search, scroll,
 * save-guard, save and close handlers.
 *
 * @param props - onClose, open, the study data, the updateStudyData callback and the drawerKey.
 * @returns An object carrying the drawer state and setter, the aborted-study flag, and the change,
 * search, scroll, save and close handlers.
 */
export const useEditDiagnosisDrawer = ({
  onClose,
  open,
  data,
  updateStudyData,
  drawerKey,
}: Pick<
  EditDiagnosisDrawerProps,
  'onClose' | 'open' | 'data' | 'updateStudyData' | 'drawerKey'
>) => {
  const [state, setState] = useSetState<MinimalState>(initalState);

  /**
   * Holds the diagnosis values the drawer was opened with, so edits can be compared against and restored from them. Created as a typed mutable ref with no initial value.
   *
   * @returns Mutable ref holding the stored diagnosis snapshot.
   */
  const storeData = (useRef as <T>() => React.MutableRefObject<T | undefined>)<StoreData>();
  const previousPropsData = useRef<DataProps | null>(null);

  const timeoutSearchPrimaryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutSearchSecondRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isAbortedStudy = data?.status === STUDY_STATUS.ABORTED;

  /**
   * Whether the diagnosis fields differ from the loaded snapshot. Memoised comparison of the current primary code, second code and trimmed patient medications against the values kept in the snapshot ref.
   *
   * @returns True when at least one diagnosis field has been edited.
   */
  const isEditStudy = React.useMemo(
    () =>
      !(
        state.primary === storeData.current?.primary &&
        state.second === storeData.current?.second &&
        state.patientMedications.trim() === storeData.current?.patientMedications.trim()
      ),
    [state.primary, state.second, state.patientMedications, storeData.current],
  );

  /**
   * Applies a field change in the edit-diagnosis drawer. Rejects a patientMedications value over 200 characters by recording the length error, otherwise stores the text and clears that error, normalises an empty primary or second diagnosis code to undefined, and writes any other field straight to state.
   *
   * @param name - Name of the form field that changed.
   * @param value - New value for that field.
   * @returns Nothing.
   */
  const handleChange = (name: string, value: any) => {
    if (name === 'patientMedications') {
      if ((value || '').length > 200) {
        setState({
          listError: { ...state.listError, patientMedications: 'Maximum 200 characters allowed.' },
        });
        return;
      }
      setState((prevState) => ({
        patientMedications: value,
        listError: { ...prevState.listError, patientMedications: '' },
      }));
      return;
    }

    if (name === 'primary') {
      const next = value || undefined;
      setState({ primary: next });
      return;
    }

    if (name === 'second') {
      const next = value || undefined;
      setState({ second: next });
      return;
    }

    setState({ [name]: value } as any);
  };

  /**
   * Debounces the term typed into the primary diagnosis code select. Clears any pending timer and 500ms later stores the term, or resets both the term and the loaded primary codes when it is empty.
   *
   * @param value - Search text typed into the primary select.
   * @returns Nothing.
   */
  const handleSearchSelectPrimary = (value: string) => {
    if (timeoutSearchPrimaryRef.current) clearTimeout(timeoutSearchPrimaryRef.current);
    timeoutSearchPrimaryRef.current = setTimeout(() => {
      if (!_.isEmpty(value)) {
        setState({ searchPrimary: value });
      } else {
        setState({ searchPrimary: '', diagnosisCodesPrimary: [] });
      }
    }, 500);
  };

  /**
   * Debounces the term typed into the secondary diagnosis code select. Clears any pending timer and 500ms later stores the term, or resets both the term and the loaded second codes when it is empty.
   *
   * @param value - Search text typed into the secondary select.
   * @returns Nothing.
   */
  const handleSearchSelectSecond = (value: string) => {
    if (timeoutSearchSecondRef.current) clearTimeout(timeoutSearchSecondRef.current);
    timeoutSearchSecondRef.current = setTimeout(() => {
      if (!_.isEmpty(value)) {
        setState({ searchSecond: value });
      } else {
        setState({ searchSecond: '', diagnosisCodesSecond: [] });
      }
    }, 500);
  };

  /**
   * Loads the next page of primary diagnosis codes when its dropdown is scrolled to the bottom. Compares scrollTop plus clientHeight against scrollHeight, then fetches more primary codes with the current search terms and already loaded lists and merges the result into state around the isLoadingPrimary flag.
   *
   * @param e - Scroll event of the primary diagnosis code dropdown.
   * @returns A promise resolving once the extra codes have been merged.
   */
  const handlePopupScrollPrimary = async (e: PopupScrollEvent) => {
    const { target } = e;
    if (target.scrollTop + target.clientHeight === target.scrollHeight) {
      setState({ isLoadingPrimary: true });
      const newState = await handleFetchDiagnosisCodes({
        type: 'primary',
        isLoadMore: true,
        searchPrimary: state.searchPrimary,
        searchSecond: state.searchSecond,
        diagnosisCodesPrimary: state.diagnosisCodesPrimary,
        diagnosisCodesSecond: state.diagnosisCodesSecond,
      });
      setState({ ...newState, isLoadingPrimary: false });
    }
  };

  /**
   * Loads the next page of secondary diagnosis codes when its dropdown is scrolled to the bottom. Compares scrollTop plus clientHeight against scrollHeight, then fetches more second codes with the current search terms and already loaded lists and merges the result into state around the isLoadingSecond flag.
   *
   * @param e - Scroll event of the secondary diagnosis code dropdown.
   * @returns A promise resolving once the extra codes have been merged.
   */
  const handlePopupScrollSecond = async (e: PopupScrollEvent) => {
    const { target } = e;
    if (target.scrollTop + target.clientHeight === target.scrollHeight) {
      setState({ isLoadingSecond: true });
      const newState = await handleFetchDiagnosisCodes({
        type: 'second',
        isLoadMore: true,
        searchPrimary: state.searchPrimary,
        searchSecond: state.searchSecond,
        diagnosisCodesPrimary: state.diagnosisCodesPrimary,
        diagnosisCodesSecond: state.diagnosisCodesSecond,
      });
      setState({ ...newState, isLoadingSecond: false });
    }
  };

  /**
   * Seeds the edit-diagnosis drawer from the study being viewed. Snapshots the report due date as a moment plus the primary and second diagnosis codes and the patient medications into storeData.current, then applies a deep clone of that snapshot to state and drops the initiating flag.
   *
   * @returns A promise resolving once the initial state has been applied.
   */
  const initiateState = async () => {
    setState({ isInitiating: true });

    const { info } = data;
    const { diagnosis } = info || {};

    storeData.current = {
      reportDueDate: convertToMoment({ isoDate: data.reportDueDate }),

      primary: diagnosis?.primary ?? null,
      second: diagnosis?.second ?? null,
      patientMedications: diagnosis?.patientMedications || '',
    } as any;

    setState({
      ..._.cloneDeep(storeData.current),
      isInitiating: false,
    } as any);
  };

  /**
   * Collects the field errors of the diagnosis form. Records a patientMedications error when the note exceeds 200 characters and returns the resulting map.
   *
   * @returns Map of field name to error message, empty when valid.
   */
  const validateData = () => {
    const listError: Record<string, string> = {};

    if ((state.patientMedications || '').length > 200) {
      listError.patientMedications = 'Maximum 200 characters allowed.';
    }

    return listError;
  };

  /**
   * Persists the edited study diagnosis. Sends the study ID with the converted payload, then closes the confirm modal, toasts success, pushes the returned diagnosis back to the study data under the diagnosis drawer key and refreshes storeData.current with the saved codes and trimmed medications; a failure logs and toasts an error, and the saving flag is always cleared.
   *
   * @returns A promise resolving once the update attempt has finished.
   */
  const onSave = async () => {
    setState({ isSaving: true });
    try {
      const input = {
        studyId: data.id,
        ...convertPayload(state),
      };
      const result = (await handleUpdateStudyDiagnosis(input)) as UpdateStudyDiagnosisResult;
      setState({ isOpenConfirmModal: false });
      const { diagnosis } = result?.study?.info || {};
      showMessageSuccess({ content: t`Diagnosis updated successfully.` });
      updateStudyData(
        {
          diagnosis: diagnosis
            ? {
                primary: diagnosis.primary ?? undefined,
                second: diagnosis.second ?? undefined,
                patientMedications: diagnosis.patientMedications ?? undefined,
              }
            : undefined,
        },
        EDIT_DRAWER_KEY.DIAGNOSIS,
      );

      storeData.current = {
        ...storeData.current,
        primary: diagnosis?.primary ?? null,
        second: diagnosis?.second ?? null,
        patientMedications: diagnosis?.patientMedications?.trim() || '',
      } as any;
    } catch (error) {
      console.error('Failed to update Diagnosis:', error);
      showMessageError({ content: t`Failed to update Diagnosis` });
    } finally {
      setState({ isSaving: false });
    }
  };

  /**
   * Guards saving the diagnosis form. Stops with a no-changes message when the drawer was not edited, writes the medications length errors to state when validateData reports any, and only then opens the confirmation modal.
   *
   * @returns Nothing.
   */
  const handleClickSave = () => {
    if (!isEditStudy) {
      showMessageInfo({ content: t`No changes were made.` });
      return;
    }

    const listError = validateData();
    if (!_.isEmpty(listError)) {
      setState({ listError });
      return;
    }

    setState({ isOpenConfirmModal: true });
  };

  /**
   * Closes the edit-diagnosis drawer. Invokes the onClose prop with the drawer key when one was supplied.
   *
   * @returns Nothing.
   */
  const handleOnClose = () => {
    if (onClose) {
      onClose(drawerKey);
    }
  };

  useEffect(() => {
    if (!open) return;

    setState({
      ...initalState,
      ..._.cloneDeep(storeData.current),
    });
  }, [open]);

  useEffect(() => {
    const dataToCompare = _.pick(_.cloneDeep(data), ['id', 'info.diagnosis']);
    const previousDataToCompare = _.pick(_.cloneDeep(previousPropsData.current), [
      'id',
      'info.diagnosis',
    ]);
    if (!_.isEqual(dataToCompare, previousDataToCompare) && open) {
      previousPropsData.current = _.cloneDeep(data);
      initiateState();
    }
  }, [data, open]);

  useUpdateEffect(async () => {
    if (!_.isEmpty(state.searchPrimary)) {
      setState({ isLoadingPrimary: true, diagnosisCodesPrimary: [] });
      const newState = await handleFetchDiagnosisCodes({
        type: 'primary',
        searchPrimary: state.searchPrimary,
        searchSecond: state.searchSecond,
        diagnosisCodesPrimary: state.diagnosisCodesPrimary,
        diagnosisCodesSecond: state.diagnosisCodesSecond,
      });
      setState({ ...newState, isLoadingPrimary: false });
    }
  }, [state.searchPrimary]);

  useUpdateEffect(async () => {
    if (!_.isEmpty(state.searchSecond)) {
      setState({ isLoadingSecond: true, diagnosisCodesSecond: [] });
      const newState = await handleFetchDiagnosisCodes({
        type: 'second',
        searchPrimary: state.searchPrimary,
        searchSecond: state.searchSecond,
        diagnosisCodesPrimary: state.diagnosisCodesPrimary,
        diagnosisCodesSecond: state.diagnosisCodesSecond,
      });
      setState({ ...newState, isLoadingSecond: false });
    }
  }, [state.searchSecond]);

  return {
    state,
    setState,
    isAbortedStudy,
    handleChange,
    handleSearchSelectPrimary,
    handleSearchSelectSecond,
    handlePopupScrollPrimary,
    handlePopupScrollSecond,
    handleClickSave,
    handleOnClose,
    onSave,
  };
};
